// src/utils/materialUtils.js

import { 
  PBRMaterial, 
  Texture, 
  Color3, 
  Color4,
  CubeTexture
} from '@babylonjs/core';

// -------------------------------------
// Define mesh groups
// -------------------------------------
export const MeshGroups = {
  FLOOR: 'Floor',
  WALL: 'Wall',
  OBJECT: 'Object',
  DOOR: 'Door',
  WINDOW: 'Window',
};

// -------------------------------------
// Restructured list of materials
// -------------------------------------
export const availableMaterials = [
  {
    name: 'Ceramics long white',
    type: 'General',
    group: MeshGroups.WALL,
    allowedGroups: [MeshGroups.WALL],

    // Common textures
    albedoPath: '/textures/walls/TilesMosaicYubi003_COL_8K.png',
    normalPath: '/textures/walls/TilesMosaicYubi003_NRM_8K.png',

    // Spec/Gloss or Roughness/Metal:
    glossPath: '/textures/walls/TilesMosaicYubi003_GLOSS_8K.png',
    reflectionPath: '/textures/walls/TilesMosaicYubi003_REFL_8K.png',
    aoPath: '/textures/walls/TilesMosaicYubi003_AO_8K.png',

    // Parallax (optional)
    displacementPath: '/textures/walls/TilesMosaicYubi003_DISP_8K.png',
    displacement16Path: '/textures/walls/TilesMosaicYubi003_DISP16_8K.png',

    // Bump (fallback if no normal map)
    bumpPath: '/textures/walls/TilesMosaicYubi003_BUMP_8K.png',
    bump16Path: '/textures/walls/TilesMosaicYubi003_BUMP16_8K.png',

    // ID map (rarely used at runtime)
    idMapPath: '/textures/walls/TilesMosaicYubi003_IDMAP_8K.png',

    // Preview sphere (reference only)
    spherePreviewPath: '/textures/walls/TilesMosaicYubi003_Sphere.png',

    // Additional PBR config
    invertNormalMapY: false,
    useParallaxOcclusion: true,       // set to true for parallax
    parallaxScaleBias: 1,            // you can adjust if needed
    roughness: 0.1,
    metallic: 0.0,
    alpha: null,

    // If color is `null` or an invalid object, we skip assigning it
    color: null,
  },
];

// --------------------------------------------------
// Helper: applyConfiguredMaterial (UPDATED VERSION)
// --------------------------------------------------
export const applyConfiguredMaterial = (mesh, scene, materialInfo) => {
  const material = new PBRMaterial(`${mesh.name}-material`, scene);

  // --------------------------------
  // Set some recommended defaults:
  // --------------------------------
  // Many PBR pipelines prefer specular over alpha to avoid weird transparency artifacts.
  material.useSpecularOverAlpha = true;
  // Usually, we keep alpha separate unless explicitly using albedo alpha:
  material.useAlphaFromAlbedoTexture = materialInfo.useAlphaFromAlbedoTexture ?? false;

  // --------------------------------
  // ALBEDO (Base Color)
  // --------------------------------
  if (materialInfo.albedoPath) {
    const albedoTex = new Texture(materialInfo.albedoPath, scene);
    albedoTex.uScale = materialInfo.tiling || 1;
    albedoTex.vScale = materialInfo.tiling || 1;
    albedoTex.anisotropicFilteringLevel = 16;
    material.albedoTexture = albedoTex;
  }

  // --------------------------------
  // NORMAL MAP (or Bump fallback)
  // --------------------------------
  if (materialInfo.normalPath) {
    const normalTex = new Texture(materialInfo.normalPath, scene);
    normalTex.invertNormalMapY = !!materialInfo.invertNormalMapY;
    normalTex.uScale = materialInfo.tiling || 1;
    normalTex.vScale = materialInfo.tiling || 1;
    normalTex.anisotropicFilteringLevel = 16;
    material.bumpTexture = normalTex;
  } else if (materialInfo.bumpPath) {
    // Fallback if no normal map is provided
    const bumpTex = new Texture(materialInfo.bumpPath, scene);
    bumpTex.uScale = materialInfo.tiling || 1;
    bumpTex.vScale = materialInfo.tiling || 1;
    bumpTex.anisotropicFilteringLevel = 16;
    material.bumpTexture = bumpTex;
  }

  // --------------------------------
  // METAL/ROUGH WORKFLOW
  // (for materials that only have
  //  diffuse/normal/roughness)
  // --------------------------------
  if (materialInfo.roughnessPath) {
    const roughTex = new Texture(materialInfo.roughnessPath, scene);
    roughTex.uScale = materialInfo.tiling || 1;
    roughTex.vScale = materialInfo.tiling || 1;
    roughTex.anisotropicFilteringLevel = 16;
    material.metallicRoughnessTexture = roughTex;

    // By default: roughness = G, metallic = B
    // If your texture is strictly roughness only, set metallic channel usage to false
    material.useRoughnessFromMetallicTextureGreen = true;
    material.useMetallnessFromMetallicTextureBlue = false;
  }

  // --------------------------------
  // SPEC/GLOSS WORKFLOW
  // (for materials that need spec/gloss + reflection)
  // --------------------------------
  if (materialInfo.glossPath) {
    const glossTex = new Texture(materialInfo.glossPath, scene);
    glossTex.uScale = materialInfo.tiling || 1;
    glossTex.vScale = materialInfo.tiling || 1;
    glossTex.anisotropicFilteringLevel = 16;
    // Spec/Gloss in Babylon
    material.specularGlossinessTexture = glossTex;
  }

  if (materialInfo.reflectionPath) {
    const reflectionTex = new Texture(materialInfo.reflectionPath, scene);
    reflectionTex.uScale = materialInfo.tiling || 1;
    reflectionTex.vScale = materialInfo.tiling || 1;
    reflectionTex.anisotropicFilteringLevel = 16;
    // Reflectivity is often used with Spec/Gloss
    material.reflectivityTexture = reflectionTex;
  }

  // --------------------------------
  // AMBIENT OCCLUSION (AO)
  // --------------------------------
  if (materialInfo.aoPath) {
    const aoTex = new Texture(materialInfo.aoPath, scene);
    aoTex.uScale = materialInfo.tiling || 1;
    aoTex.vScale = materialInfo.tiling || 1;
    material.ambientTexture = aoTex;
  }

  // --------------------------------
  // HEIGHT / DISPLACEMENT (Optional)
  // Parallax Occlusion uses the bump slot
  // --------------------------------
  if (materialInfo.displacementPath || materialInfo.displacement16Path) {
    const dispPath = materialInfo.displacement16Path || materialInfo.displacementPath;
    const dispTex = new Texture(dispPath, scene);
    dispTex.uScale = materialInfo.tiling || 1;
    dispTex.vScale = materialInfo.tiling || 1;

    material.parallaxScaleBias = materialInfo.parallaxScaleBias ?? 0.01;
    material.useParallaxOcclusion = !!materialInfo.useParallaxOcclusion;

    // Use bump slot for parallax-based displacement
    material.bumpTexture = dispTex;
  }

  // --------------------------------
  // BASIC PBR PROPERTIES (Color, Roughness, Metallic)
  // --------------------------------
  if (materialInfo.color !== undefined && materialInfo.color !== null) {
    // If it's already a Color3/Color4, assign directly; 
    // otherwise, build a Color3 from {r,g,b}
    if (materialInfo.color instanceof Color3 || materialInfo.color instanceof Color4) {
      material.albedoColor = materialInfo.color;
    } else if (
      typeof materialInfo.color === 'object' && 
      materialInfo.color.r !== undefined && 
      materialInfo.color.g !== undefined && 
      materialInfo.color.b !== undefined
    ) {
      material.albedoColor = new Color3(
        materialInfo.color.r,
        materialInfo.color.g,
        materialInfo.color.b
      );
    } else {
      console.warn(
        `[materialUtils] Invalid color provided for "${materialInfo.name}", skipping...`
      );
    }
  }

  // If not provided, default to a typical "ceramic" look
  material.roughness = 
    materialInfo.roughness !== undefined 
      ? materialInfo.roughness 
      : 0.1;

  material.metallic = 
    materialInfo.metallic !== undefined 
      ? materialInfo.metallic 
      : 0.0;

  // --------------------------------
  // TRANSPARENCY (Alpha)
  // --------------------------------
  if (materialInfo.alpha !== undefined && materialInfo.alpha !== null) {
    material.alpha = materialInfo.alpha;

    // Handle alpha blending for windows/doors
    if (materialInfo.type === 'Window' || materialInfo.type === 'Door') {
      material.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHABLEND;
      material.needAlphaBlending = true;
      material.backFaceCulling = false;
    }
  }

  // --------------------------------
  // DOUBLE-SIDED WALLS
  // --------------------------------
  if (mesh.group === MeshGroups.WALL) {
    material.backFaceCulling = false;
    material.twoSidedLighting = true;
  }

  // --------------------------------
  // Dispose old material(s)
  // --------------------------------
  if (mesh.material) {
    if (Array.isArray(mesh.material)) {
      mesh.material.forEach((oldMat) => oldMat && oldMat.dispose());
    } else {
      mesh.material.dispose();
    }
  }

  // --------------------------------
  // Assign the new PBR material
  // --------------------------------
  mesh.material = material;
};

// --------------------------------
// Floor-specific material setup
// --------------------------------
export const applyFloorMaterial = (mesh, scene, type, log, warn) => {
  const floorMaterialInfo = availableMaterials.find(
    (mat) => mat.name === type && mat.type === 'Floor'
  );
  if (!floorMaterialInfo) {
    warn(`Floor material type '${type}' not found. Applying default floor material.`);
    return;
  }
  applyConfiguredMaterial(mesh, scene, floorMaterialInfo);
  console.log(`Applied '${type}' material to the floor mesh.`);
};

// --------------------------------
// General material application
// --------------------------------
export const applyMaterialToMesh = (mesh, scene, materialInfo, log, warn) => {
  // Validate if the material is allowed for this mesh
  const isAllowed =
    materialInfo.allowedGroups.includes(mesh.group) &&
    (!materialInfo.allowedMeshes || materialInfo.allowedMeshes.includes(mesh.name));

  if (!isAllowed) {
    warn(`Material '${materialInfo.name}' is not allowed for mesh '${mesh.name}'.`);
    return;
  }

  applyConfiguredMaterial(mesh, scene, materialInfo);
  mesh.currentMaterial = materialInfo.name;
  console.log(`Applied material '${materialInfo.name}' to mesh '${mesh.name}'.`);
};
